// 画像からのデッキ読み取り（worker の /decks/scan-image）の前後処理。
// 送信前の画像縮小と、読み取ったカード名 → カードDB の照合を担当する。
import { getAllCards, stripRuby } from "@dm-online/shared";
import type { Card } from "@dm-online/shared";

// worker が返す読み取り結果
export interface DeckScanResult {
  cards: { name: string; count: number }[];
  notes: string;
}

// =====================
// 画像の縮小
// =====================

// Claude の画像入力は長辺 1568px 程度で十分。それ以上は送っても縮小されるだけなので手前で落とす
const MAX_EDGE = 1568;

export async function resizeImage(file: Blob): Promise<{ data: string; mediaType: "image/jpeg" }> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("画像を処理できませんでした");
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
  return { data: dataUrl.slice(dataUrl.indexOf(",") + 1), mediaType: "image/jpeg" };
}

// =====================
// カード名の照合
// =====================

// ルビ・全角/半角・中黒・空白・長音の揺れを吸収したキー
export function nameKey(name: string): string {
  return stripRuby(name)
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[-‐‑–—―−ｰ~〜]/g, "ー")
    .replace(/[・･\s「」『』"'`]/g, "");
}

function bigrams(key: string): Set<string> {
  const out = new Set<string>();
  if (key.length === 1) out.add(key);
  for (let i = 0; i < key.length - 1; i++) out.add(key.slice(i, i + 2));
  return out;
}

// 2つの bigram 集合の Dice 係数（0〜1）
function dice(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let common = 0;
  for (const g of a) if (b.has(g)) common++;
  return (2 * common) / (a.size + b.size);
}

interface IndexedCard {
  card: Card;
  key: string;
  grams: Set<string>;
}

let index: { byKey: Map<string, Card>; list: IndexedCard[] } | null = null;

function getIndex() {
  if (!index) {
    const byKey = new Map<string, Card>();
    const list: IndexedCard[] = [];
    for (const card of getAllCards()) {
      const key = nameKey(card.name);
      if (!byKey.has(key)) byKey.set(key, card);
      list.push({ card, key, grams: bigrams(key) });
    }
    index = { byKey, list };
  }
  return index;
}

// 類似度がこの値以上なら「たぶんこのカード」として自動で当てる（OCR 的な1〜2文字の読み違いを救う）
const AUTO_MATCH_SCORE = 0.6;
const CANDIDATE_COUNT = 5;

export interface CardNameMatch {
  card: Card | null;
  // exact: 名前が一致 / fuzzy: 類似度で推定（要確認） / none: 見つからない
  confidence: "exact" | "fuzzy" | "none";
  // 差し替え用の候補（類似度順。card を含む）
  candidates: Card[];
}

export function matchCardName(name: string): CardNameMatch {
  const { byKey, list } = getIndex();
  const key = nameKey(name);
  if (!key) return { card: null, confidence: "none", candidates: [] };

  const grams = bigrams(key);
  const scored = list
    .map((entry) => ({ entry, score: dice(grams, entry.grams) }))
    .filter((s) => s.score > 0.2)
    .sort((a, b) => b.score - a.score)
    .slice(0, CANDIDATE_COUNT);
  const candidates = scored.map((s) => s.entry.card);

  const exact = byKey.get(key);
  if (exact) {
    return { card: exact, confidence: "exact", candidates: [exact, ...candidates.filter((c) => c.id !== exact.id)] };
  }

  const best = scored[0];
  if (best && best.score >= AUTO_MATCH_SCORE) {
    return { card: best.entry.card, confidence: "fuzzy", candidates };
  }
  return { card: null, confidence: "none", candidates };
}
